import {Injectable} from '@angular/core';
import {Rule, RuleGroup} from '../models/form-schema.model';

@Injectable({
  providedIn: 'root'
})
export class RulesEngineService {
  evaluateRuleGroup(ruleGroup: RuleGroup, values: any): boolean {
    if (!ruleGroup || !ruleGroup.rules || ruleGroup.rules.length === 0) {
      return true;
    }

    if (ruleGroup.condition === 'AND') {
      return ruleGroup.rules.every(rule => this.evaluate(rule, values));
    } else {
      return ruleGroup.rules.some(rule => this.evaluate(rule, values));
    }
  }

  private evaluate(rule: Rule | RuleGroup, values: any): boolean {
    if ('condition' in rule) {
      return this.evaluateRuleGroup(rule as RuleGroup, values);
    }
    return this.evaluateSingleRule(rule as Rule, values);
  }

  private evaluateSingleRule(rule: Rule, values: any): boolean {
    let val = this.getValueByPath(values, rule.fieldKey);

    // Normalized rule value for more reliable comparison
    let ruleValue = rule.value;

    // If the rule value looks like a boolean, ensure it's a real boolean for comparison
    if (typeof ruleValue === 'string') {
      const normalizedRuleVal = ruleValue.toLowerCase().trim();
      if (normalizedRuleVal === 'true') {
        ruleValue = true;
      } else if (normalizedRuleVal === 'false') {
        ruleValue = false;
      }
    }

    // Normalized incoming value from the form
    let normalizedVal = val;

    // Special handling for boolean comparison
    if (typeof ruleValue === 'boolean') {
      if (normalizedVal === undefined || normalizedVal === null || normalizedVal === '') {
        normalizedVal = false;
      } else if (typeof normalizedVal === 'string') {
        const lowerVal = normalizedVal.toLowerCase().trim();
        normalizedVal = lowerVal === 'true' || lowerVal === 'checked' || lowerVal === '1';
      } else {
        // Force to boolean
        normalizedVal = !!normalizedVal;
      }
    }

    // Handle numeric conversion if needed
    if (typeof normalizedVal === 'number' && typeof ruleValue === 'string') {
      const numValue = parseFloat(ruleValue);
      if (!isNaN(numValue)) {
        ruleValue = numValue;
      }
    }

    // Date normalization for comparison
    // We check if either value looks like a date string (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    const isDateVal = typeof normalizedVal === 'string' && dateRegex.test(normalizedVal);
    const isDateRule = typeof ruleValue === 'string' && dateRegex.test(ruleValue);

    const comparisonOps = ['greaterThan', 'greaterThanOrEqual', 'lessThan', 'lessThanOrEqual'];
    if (isDateVal && isDateRule && comparisonOps.includes(rule.operator)) {
      // Compare dates by timestamp for ordering operators.
      const dVal = new Date(normalizedVal).getTime();
      const dRule = new Date(ruleValue).getTime();
      return this.compare(dVal, dRule, rule.operator);
    }

    return this.compare(normalizedVal, ruleValue, rule.operator);
  }

  private isEmpty(val: any): boolean {
    return val === undefined || val === null || val === ''
      || (Array.isArray(val) && val.length === 0);
  }

  private compare(val: any, ruleValue: any, operator: string): boolean {
    switch (operator) {
      case 'equals':
        return val === ruleValue;
      case 'notEquals':
        return val !== ruleValue;
      case 'contains':
        if (Array.isArray(val)) {
          return val.includes(ruleValue);
        }
        if (typeof val === 'string') {
          return val.includes(String(ruleValue));
        }
        return false;
      case 'startsWith':
        return typeof val === 'string' && val.startsWith(String(ruleValue));
      case 'greaterThan':
        return val > ruleValue;
      case 'greaterThanOrEqual':
        return val >= ruleValue;
      case 'lessThan':
        return val < ruleValue;
      case 'lessThanOrEqual':
        return val <= ruleValue;
      case 'isEmpty':
        return this.isEmpty(val);
      case 'isNotEmpty':
        return !this.isEmpty(val);
      default:
        return true;
    }
  }

  private getValueByPath(obj: any, path: string): any {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((o, i) => (o && o[i] !== undefined ? o[i] : undefined), obj);
  }
}
